<script setup lang="ts">
import UIButton from '@/shared/ui/Button';
import { scrollToTop } from '@/shared/utils/scrollBehavior';
import type { IPaginationItem, Props } from '../model/types/pagination';

const props = defineProps<Props>();
const { t } = useI18n();

const emit = defineEmits<{
  (e: 'update:modelValue', page: number): void;
  (e: 'on-change', page: number): void;
  (e: 'on-load-more', page: number): void;
}>();

const currentPage = computed(() => props.modelValue);
const range = computed(() => props.pageRange ?? 2);

const pages = computed<IPaginationItem[]>(() => {
  const arr: IPaginationItem[] = [];
  const left = Math.max(2, currentPage.value - range.value);
  const right = Math.min(props.totalPages - 1, currentPage.value + range.value);

  if (left > 2) arr.push({ isMore: true, key: 'more-left' });
  for (let p = left; p <= right; p++) {
    arr.push({ page: p, isMore: false, key: `page-${p}` });
  }
  if (right < props.totalPages - 1) arr.push({ isMore: true, key: 'more-right' });

  return arr;
});

const goTo = (page: number) => {
  if (page >= 1 && page <= props.totalPages && page !== currentPage.value) {
    emit('update:modelValue', page);
    emit('on-change', Number(page));
    scrollToTop('smooth');
  }
};

const onLoadMore = () => {
  const nextPage = currentPage.value + 1;
  emit('update:modelValue', nextPage);
  emit('on-load-more', Number(nextPage));
};
</script>

<template>
  <div class="pagination">
    <UIButton
      v-if="currentPage < totalPages"
      class="pagination__load-more"
      light-orange
      small
      :text="loadMoreText"
      @click="onLoadMore"
    />
    <nav class="pagination__nav">
      <button
        v-if="currentPage !== 1"
        class="pagination__button _prev"
        :aria-label="t('arrowBack')"
        @click="goTo(currentPage - 1)"
      >
        {{ t('back') }}
      </button>
      <button
        class="pagination__button"
        :class="{ _active: currentPage === 1 }"
        :aria-label="t('goToFirstPage')"
        @click="goTo(1)"
      >
        1
      </button>
      <template v-for="item in pages" :key="item.key">
        <span v-if="item.isMore" class="pagination__button _more">…</span>
        <button
          v-else
          class="pagination__button"
          :class="{ _active: item.page === currentPage }"
          :aria-label="`${t('goToPage')} ${item.page}`"
          @click="goTo(item.page!)"
        >
          {{ item.page }}
        </button>
      </template>
      <button
        v-if="totalPages > 1"
        class="pagination__button"
        :class="{ _active: currentPage === totalPages }"
        :aria-label="t('goToLastPage')"
        @click="goTo(totalPages)"
      >
        {{ totalPages }}
      </button>
      <button
        v-if="currentPage < totalPages"
        class="pagination__button _next"
        :aria-label="t('arrowForward')"
        @click="goTo(currentPage + 1)"
      >
        {{ t('forward') }}
      </button>
    </nav>
  </div>
</template>

<style lang="less" scoped>
.pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;

  &__nav {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 15px;
    line-height: 100%;
  }

  &__load-more {
    max-width: 163px;
  }

  &__button {
    padding: 4px 8px;
    margin: 0 4px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.42);

    &._more {
      cursor: default;
    }

    &._active {
      color: @color-cod-gray;
    }

    &:disabled {
      color: #ccc;
      cursor: not-allowed;
    }
  }
}
</style>
